import { Component } from "@theme/component";
import {
  ThemeEvents,
  VariantUpdateEvent,
  ZoomMediaSelectedEvent,
} from "@theme/events";

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest(".shopify-section, dialog");

    target?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#handleVariantUpdate,
      { signal }
    );
    this.refs.zoomDialogComponent?.addEventListener(
      ThemeEvents.zoomMediaSelected,
      this.#handleZoomMediaSelected,
      {
        signal,
      }
    );

    // Setup resize observer for square mode to ensure carousel re-measures correctly
    this.#setupSquareModeResizeHandler();
  }

  #controller = new AbortController();
  /** @type {ResizeObserver|null} */
  #resizeObserver = null;
  /** @type {ReturnType<typeof setTimeout>|null} */
  #resizeTimeout = null;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();

    if (this.#resizeTimeout) {
      clearTimeout(this.#resizeTimeout);
      this.#resizeTimeout = null;
    }

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
      this.#resizeObserver = null;
    }
  }

  /**
   * Sets up a resize observer for square mode to ensure the carousel
   * re-measures correctly when the container size changes.
   * This is especially important for the Boxed Product Page where
   * square media needs to stay square on large screens.
   */
  #setupSquareModeResizeHandler() {
    // Check if we're in square mode
    const style = this.getAttribute("style") || "";
    const isSquareMode =
      style.includes("--gallery-aspect-ratio: 1") ||
      style.includes("--gallery-aspect-ratio:1");

    if (!isSquareMode) return;

    // Use ResizeObserver to detect when the square container changes size
    this.#resizeObserver = new ResizeObserver(() => {
      // Debounce the resize handling
      if (this.#resizeTimeout) {
        clearTimeout(this.#resizeTimeout);
      }

      this.#resizeTimeout = setTimeout(() => {
        // Trigger a re-sync of the slideshow if it exists
        // The slideshow component has internal ResizeObserver, but we need to
        // ensure it re-measures when our square container changes
        if (this.slideshow) {
          // Dispatch a custom event that the slideshow can listen to
          this.slideshow.dispatchEvent(new CustomEvent("media-gallery:resize"));
        }
      }, 100);
    });

    // Observe the media gallery itself for size changes
    if (this.#resizeObserver) {
      this.#resizeObserver.observe(this);
    }
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector("media-gallery");

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get("media-gallery")) {
  customElements.define("media-gallery", MediaGallery);
}
